import type {
  CharacterValues,
  Specialization,
  SpecImmunity,
  SpecAreaControl,
} from '../../types/character_values'
import type CharacterRole from '../../types/custom/CharacterRole'
import characterValuesData from '../../../../config/static_data/character_values.json'

const characterValues: CharacterValues =
  characterValuesData as unknown as CharacterValues

const GUARDIAN_SPEC_ID = '104'
const FERAL_SPEC_ID = '103'
const versionsWithoutGuardian = ['classic_era', 'classic_anniversary']

function withFeralLabelForGuardian(
  specId: string,
  spec: Specialization,
): Specialization {
  if (specId !== GUARDIAN_SPEC_ID) return spec
  if (!versionsWithoutGuardian.includes(window.page_info?.gameVersion))
    return spec
  const feral = characterValues.specializations[FERAL_SPEC_ID]
  return { ...spec, name: feral.name, icon: feral.icon }
}

export function getCharacterValues(): CharacterValues {
  return characterValues
}

export function getClassicDefaultRoleForClass(classId: string): string {
  return characterValues.classic_default_role_for_class[classId] || 'melee'
}

export function getSpecialization(specId: string): Specialization | undefined {
  const spec = characterValues.specializations[specId]
  return spec ? withFeralLabelForGuardian(specId, spec) : spec
}

export function getClass(classId: string): string | undefined {
  return characterValues.classes[classId]
}

export function getFaction(factionId: string): string | undefined {
  return characterValues.factions[factionId]
}

export function getRace(raceId: string): string | undefined {
  return characterValues.races[raceId]
}

export function getAllClasses(): Record<string, string> {
  return characterValues.classes
}

export function getAllFactions(): Record<string, string> {
  return characterValues.factions
}

export function getAllRaces(): Record<string, string> {
  return characterValues.races
}

export function getAllSpecializations(): Record<string, Specialization> {
  return Object.fromEntries(
    Object.entries(characterValues.specializations).map(([specId, spec]) => [
      specId,
      withFeralLabelForGuardian(specId, spec),
    ]),
  )
}

export function canCharacterHeal(classId?: number): boolean {
  const specializations = getSpecializationsByClass(classId)
  return Object.values(specializations).some(
    (spec) => spec.default_role === 'heal',
  )
}

export function getSpecializationsByClass(
  classId?: number,
): Record<string, Specialization> {
  const specializations = getAllSpecializations()
  const classSpecializations: Record<string, Specialization> = {}

  Object.entries(specializations).forEach(([specId, spec]) => {
    if (spec.class_id === classId) {
      classSpecializations[specId] = spec
    }
  })

  return classSpecializations
}

export function groupSpecializationsByRole(
  classId?: number,
): Record<string, Specialization[]> {
  const specializations = getSpecializationsByClass(classId)
  const groupedByRole: Record<string, Specialization[]> = {}

  Object.values(specializations).forEach((spec) => {
    const role = spec.default_role
    if (!groupedByRole[role]) {
      groupedByRole[role] = []
    }
    groupedByRole[role].push(spec)
  })

  return groupedByRole
}

export function hasMultipleSpecsWithSameRole(classId?: number): boolean {
  const groupedByRole = groupSpecializationsByRole(classId)
  return Object.values(groupedByRole).some((specs) => specs.length > 1)
}

export function getRolesWithMultipleSpecs(classId?: number): string[] {
  const groupedByRole = groupSpecializationsByRole(classId)
  return Object.entries(groupedByRole)
    .filter(([, specs]) => specs.length > 1)
    .map(([role]) => role)
}

const specEntriesForRole = (classId: number, role: string) =>
  Object.entries(getSpecializationsByClass(classId)).filter(
    ([, spec]) => spec.default_role === role,
  )

export function getUniqueSpecIdForRole(
  classId: number | undefined,
  role: string,
): number | null {
  if (!classId) return null
  const matching = specEntriesForRole(classId, role)
  if (matching.length !== 1) return null
  return Number(matching[0][0])
}

const toSpecializationRole = (
  role?: string,
): Specialization['default_role'] | Specialization['default_role'][] | null => {
  if (role === 'tank') return 'tank'
  if (role === 'heal' || role === 'healer') return 'heal'
  if (role === 'melee') return 'melee'
  if (role === 'ranged') return 'ranged'
  if (role === 'dps') return ['melee', 'ranged']
  return null
}

export function getSpecIdForClassRole(
  classId?: number,
  role?: string,
): number | null {
  if (!classId) return null
  const requestedRoles = toSpecializationRole(role)
  if (!requestedRoles) return null
  const roles = Array.isArray(requestedRoles)
    ? requestedRoles
    : [requestedRoles]
  for (const requestedRole of roles) {
    const specId = Number(specEntriesForRole(classId, requestedRole)[0]?.[0])
    if (Number.isFinite(specId)) return specId
  }
  return null
}

export function getSpecializationInterruptCooldown(
  specId?: number | null,
): number | null {
  if (specId === undefined || specId === null) return null
  const cooldown = getSpecialization(specId.toString())?.interrupt_cooldown
  return typeof cooldown === 'number' ? cooldown : null
}

export function getSpecializationImmunity(
  specId?: number | null,
): SpecImmunity | null {
  if (specId === undefined || specId === null) return null
  const immunity = getSpecialization(specId.toString())?.has_immunity
  if (
    !immunity ||
    typeof immunity !== 'object' ||
    typeof immunity.cooldown !== 'number' ||
    typeof immunity.charges !== 'number'
  ) {
    return null
  }
  return immunity
}

export function getSpecializationAreaControl(
  specId?: number | null,
): SpecAreaControl | null {
  if (specId === undefined || specId === null) return null
  const areaControl = getSpecialization(specId.toString())?.area_control
  if (
    !areaControl ||
    typeof areaControl !== 'object' ||
    typeof areaControl.cooldown !== 'number'
  ) {
    return null
  }
  return areaControl
}

export const rolesWithMultipleSpecsTreatingDpsAsOneByClassId: Record<
  number,
  CharacterRole[]
> = (() => {
  const groupedSpecCountsByClass: Record<
    number,
    Record<CharacterRole, number>
  > = {}

  Object.values(characterValues.specializations).forEach((spec) => {
    const classId = spec.class_id
    if (!classId) return
    const role = spec.default_role as CharacterRole
    if (!groupedSpecCountsByClass[classId]) {
      groupedSpecCountsByClass[classId] = {
        tank: 0,
        heal: 0,
        melee: 0,
        ranged: 0,
      }
    }
    groupedSpecCountsByClass[classId][role] += 1
  })

  return Object.fromEntries(
    Object.entries(groupedSpecCountsByClass).map(([classId, roleCounts]) => {
      const roles: CharacterRole[] = []
      if (roleCounts.melee + roleCounts.ranged > 1) {
        roles.push('melee', 'ranged')
      }
      if (roleCounts.tank > 1) {
        roles.push('tank')
      }
      if (roleCounts.heal > 1) {
        roles.push('heal')
      }
      return [Number(classId), roles]
    }),
  )
})()

export function getRolesWithMultipleSpecsTreatingDpsAsOne(
  classId?: number,
): string[] {
  if (!classId) return []
  return rolesWithMultipleSpecsTreatingDpsAsOneByClassId[classId] ?? []
}

const HEALER_SPEC_IDS = [65, 105, 256, 257, 264, 270, 1468] as const

export function getHealerSpecializations(): Array<{
  id: number
  name: string
  icon: string
  classId: number
}> {
  return HEALER_SPEC_IDS.map((specId) => {
    const specialization = getSpecialization(specId.toString())
    if (!specialization || typeof specialization.class_id !== 'number') {
      return null
    }
    return {
      id: specId,
      name: specialization.name,
      icon: specialization.icon,
      classId: specialization.class_id,
    }
  }).filter((entry): entry is NonNullable<typeof entry> => entry !== null)
}
